<template>
  <ModalDialog
    :header-text="`Product Submission Details Job Id: ${props.jobId}`"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <Grid
        class="products-details-grid"
        :data-items="products"
        :columns="columns"
        ref="productDetailsGridRef"
        data-cy="products-grid"
      >
        <template v-slot:LabelNameCell="{ props: { dataItem } }">
          <td class="k-table-td">
            <span v-if="dataItem.RequestedLabelName">
              <span class="green"><b>Label Name: </b> </span>
              {{ dataItem.RequestedLabelName }}
            </span>
            <span
              v-if="
                dataItem.RequestedLabelName && dataItem.RequestedLabelNumber
              "
            >
              <br /><br />
            </span>
            <span v-if="dataItem.RequestedLabelNumber">
              <span class="green"><b>Label #: </b> </span>
              {{ dataItem.RequestedLabelNumber }}
            </span>
          </td>
        </template>
        <template v-slot:ProdNameCell="{ props: { dataItem } }">
          <td class="k-table-td">
            <span v-if="dataItem.RequestedProductName">
              <span class="green"><b>Prod Name: </b> </span>
              {{ dataItem.RequestedProductName }}
            </span>
            <span
              v-if="
                dataItem.RequestedProductName && dataItem.RequestedProductNumber
              "
            >
              <br /><br />
            </span>
            <span v-if="dataItem.RequestedProductNumber">
              <span class="green"><b>Prod #: </b> </span>
              {{ dataItem.RequestedProductNumber }}
            </span>
          </td>
        </template>
        <template v-slot:ProductLocCell="{ props: { dataItem } }">
          <td class="k-table-td">
            <a
              v-if="
                isAuthorizedForLocDownloads &&
                dataItem.LineStatus == 'Certified'
              "
              class="attachment-link"
              @click="downloadByUkdIdAsync(dataItem.UKDID)"
              ><font-awesome-icon icon="fa-solid fa-arrow-down" /> LOC</a
            >
          </td>
        </template>
        <template v-slot:AttachmentsCell="{ props: { dataItem } }">
          <td class="k-table-td">
            <a
              v-if="dataItem.Attachments.length > 0"
              class="attachment-link"
              :title="`Download ${dataItem.Attachments.length} Attachments`"
              @click="downloadAttachment(dataItem)"
              >{{ `Download ${dataItem.Attachments.length} Attachments` }}</a
            >
          </td>
        </template>
      </Grid>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { ref, onBeforeMount, onMounted } from "vue";
import { Grid } from "@progress/kendo-vue-grid";
import { useMainStore } from "@/stores/useMainStore";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import type { ProductSubmissionDetail } from "@/Domain/ProductSubmissionDetail";
import GridController from "@/shared/services/GridController";
import { authorizedForLocDownloads } from "@/shared/access/IsAuthorizedForLocDownloads";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";

const main = useMainStore();
const notificationStore = useNotificationStore();

const isAuthorizedForLocDownloads = authorizedForLocDownloads();

const props = defineProps<{
  jobId: string;
  companyId?: string;
}>();

const emit = defineEmits(["closeModal"]);

const products = ref<ProductSubmissionDetail[]>([]);
const productDetailsGridRef = ref<InstanceType<typeof Grid> | null>(null);

const columns = ref<GridColumnPropsExtended[]>([
  {
    title: "Line Item",
    field: "LineItem",
    minWidth: 80,
  },
  {
    title: "Label Name",
    field: "RequestedLabelName",
    cell: "LabelNameCell",
    minWidth: 80,
  },
  {
    title: "Prod Name",
    field: "RequestedProductName",
    cell: "ProdNameCell",
    minWidth: 80,
  },
  {
    title: "Brand",
    field: "RequestedBrand",
    minWidth: 80,
  },
  {
    title: "Label Types",
    field: "labelType",
    minWidth: 80,
  },
  {
    title: "UKD Id",
    field: "UKDID",
    minWidth: 80,
  },
  {
    title: "Plant Name",
    field: "PlantName",
    minWidth: 80,
  },
  {
    title: "Group",
    field: "grp",
    minWidth: 80,
  },
  {
    title: "Symbol",
    field: "symbol",
    minWidth: 80,
  },
  {
    title: "Consumer Industrial",
    field: "consumerIndustrial",
    minWidth: 80,
  },
  {
    title: "Status",
    field: "LineStatus",
    minWidth: 80,
  },
  {
    title: "Product LOC",
    cell: "ProductLocCell",
    minWidth: 80,
  },
  {
    title: "Attachments",
    cell: "AttachmentsCell",
    minWidth: 80,
  },
]);

const controller = new GridController<ProductSubmissionDetail>(
  "SubmissionProductDetailsModal",
  columns.value
);

controller.initOptions();

const getProductSubmissionDetailsAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("GetProductSubmissionDetails", {
      userLogin: main.userLogin,
      companyLogin: props.companyId ?? main.companyLogin,
      jobId: props.jobId,
    }),
    (data) => {
      products.value = data;
    },
    "ProductDetailsModal.vue",
    "getProductSubmissionDetailsAsync"
  );
};

const downloadByUkdIdAsync = async (ukdId: string) => {
  return handleResponsePromiseAsync(
    api.getDownload("DownloadLocByUkdId", {
      companyLogin: props.companyId ?? main.companyLogin,
      ukdId: ukdId,
    }),
    () => {},
    "ProductDetailsModal.vue",
    "downloadByUkdIdAsync",
    (error, response) => {
      notificationStore.notify({
        message:
          response?.error.message ?? "Error downloading LOC. Please try again.",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );
};

const downloadAttachment = function (ingredient: ProductSubmissionDetail) {
  return api.getDownload("GetSubmissionDetailsAttachment", {
    jobId: props.jobId,
    inCartId: ingredient.ProductInCartID.toString(),
    submissionType: "Product",
  });
};

const closeModal = function () {
  emit("closeModal");
};

onBeforeMount(async () => {
  await getProductSubmissionDetailsAsync();
});

onMounted(() => {
  controller.setGridRef(productDetailsGridRef);
});
</script>

<style scoped lang="scss">
.products-details-grid {
  overflow-wrap: break-word;

  .attachment-link {
    cursor: pointer;
  }

  @include media-breakpoint-up(sm) {
    max-width: 90vw;
  }

  @include media-breakpoint-up(lg) {
    width: 800px;
  }

  @include media-breakpoint-up(xl) {
    width: 1100px;
  }

  .green {
    color: green;
  }
}

td {
  font-size: 13px;
}
</style>
